import { Timer } from '@lessonup/pin-renderer';
import { pinInteractiveTypes } from '@lessonup/pins-shared';
import { pauseAllTimers } from '../../../store/utils/timer.utils';
import {
  InitializedSelfPacedPlayerStoreState,
  isInitializedSelfPacedState,
  SelfPacedPlayerState,
  SelfPacedView,
} from '../../store/selfPacedPlayerStore.types';
import { Direction } from '../selfPacedReducer.types';
import { getSelfPacedPinReducer } from './selfPacedReducerSelector';

function canArrowButtonNavigate(state: InitializedSelfPacedPlayerStoreState, direction: Direction): boolean {
  const viewLevelResult = attemptViewLevelNavigation(state, direction, { skipInteractivePins: true });
  if (viewLevelResult) {
    return true;
  }

  const targetId = attemptPinLevelNavigation(state, direction);
  return targetId !== null;
}

export function computeNavigationStateInitialized(state: InitializedSelfPacedPlayerStoreState): {
  hasPrevious: boolean;
  hasNext: boolean;
} {
  const index = state.pins.findIndex((p) => p.id === state.currentPinId);
  const isFirstPin = index === 0;
  const isLastPin = index === state.pins.length - 1;

  if (!isFirstPin && !isLastPin) return { hasPrevious: true, hasNext: true };

  const hasPrevious = canArrowButtonNavigate(state, -1);
  const hasNext = canArrowButtonNavigate(state, 1);

  return { hasPrevious, hasNext };
}

export function computeNavigationState(state: SelfPacedPlayerState): { hasPrevious: boolean; hasNext: boolean } {
  if (!isInitializedSelfPacedState(state)) return { hasNext: false, hasPrevious: false };
  return computeNavigationStateInitialized(state);
}

function initStateForPin(state: InitializedSelfPacedPlayerStoreState, pinId: string): SelfPacedPlayerState['pinState'] {
  if (state.pinState[pinId]) {
    return state.pinState;
  }

  return {
    ...state.pinState,
    [pinId]: {
      view: null,
    },
  };
}

export function stateForNextView(
  state: InitializedSelfPacedPlayerStoreState,
  pinId: string,
  nextView: SelfPacedView | null
): InitializedSelfPacedPlayerStoreState {
  if (!nextView) return state;

  const pinState = initStateForPin(state, pinId);
  pinState[pinId].view = nextView;

  return {
    ...state,
    pinState,
    currentView: nextView,
  };
}

function isPinInteractive(state: InitializedSelfPacedPlayerStoreState): boolean {
  const pin = state.pins.find((p) => p.id === state.currentPinId);
  return pin ? (pinInteractiveTypes as readonly string[]).includes(pin.type) : false;
}

interface NavigationOptions {
  skipInteractivePins: boolean;
}

function attemptViewLevelNavigation(
  state: InitializedSelfPacedPlayerStoreState,
  direction: Direction,
  options?: NavigationOptions
): InitializedSelfPacedPlayerStoreState | null {
  const pin = state.pins.find((p) => p.id === state.currentPinId);
  const reducer = pin ? getSelfPacedPinReducer(pin.type) : null;

  if (!reducer || !state.currentView) {
    return null;
  }

  if (options?.skipInteractivePins && isPinInteractive(state)) {
    return null;
  }

  const nextView = reducer.handleNextView(state, state.currentView as never, direction);

  if (!nextView) {
    return null;
  }

  return stateForNextView(state, state.currentPinId, nextView);
}

function attemptPinLevelNavigation(state: SelfPacedPlayerState, direction: Direction): string | null {
  if (!state.currentPinId) return null;
  const currentIndex = state.pins.findIndex((p) => p.id === state.currentPinId);
  const targetId = state.pins[currentIndex + direction]?.id || null;
  return targetId;
}

export function handleNavActionNewView(
  state: SelfPacedPlayerState,
  actionType: 'next' | 'previous'
): SelfPacedPlayerState {
  if (!isInitializedSelfPacedState(state)) return state;
  const direction: Direction = actionType === 'next' ? 1 : -1;

  // Skip phase transitions for interactive pins, but allow animation steps for non-interactive pins
  const viewLevelResult = attemptViewLevelNavigation(state, direction, { skipInteractivePins: true });
  if (viewLevelResult) {
    return {
      ...viewLevelResult,
      navState: computeNavigationStateInitialized(viewLevelResult),
    };
  }

  // Attempt pin-level navigation
  const targetId = attemptPinLevelNavigation(state, direction);
  if (targetId) {
    const stayedInSamePin = state.currentPinId === targetId;
    const timers = stayedInSamePin ? state.timers : pauseAllTimers(state.timers);

    const stateWithNewPin: InitializedSelfPacedPlayerStoreState = {
      ...state,
      currentPinId: targetId,
      timers,
    };

    const stateWithView = newForegroundPinSelfPaced(stateWithNewPin, targetId);

    return {
      ...stateWithView,
      navState: computeNavigationStateInitialized(stateWithView),
    };
  }

  return state;
}

export function handleCompleteInputAction(state: SelfPacedPlayerState): SelfPacedPlayerState {
  if (!isInitializedSelfPacedState(state)) return state;

  const viewLevelResult = attemptViewLevelNavigation(state, 1);
  if (viewLevelResult) {
    return {
      ...viewLevelResult,
      navState: computeNavigationStateInitialized(viewLevelResult),
    };
  }

  return {
    ...state,
    navState: computeNavigationStateInitialized(state),
  };
}

export function handleEditAnswerAction(state: SelfPacedPlayerState): SelfPacedPlayerState {
  if (!isInitializedSelfPacedState(state)) return state;

  const viewLevelResult = attemptViewLevelNavigation(state, -1);
  if (viewLevelResult) {
    return {
      ...viewLevelResult,
      navState: computeNavigationStateInitialized(viewLevelResult),
    };
  }

  return {
    ...state,
    navState: computeNavigationStateInitialized(state),
  };
}

export function handleTimerActionSelfPaced(
  state: SelfPacedPlayerState,
  action: { type: 'timer'; pinId: string; timerComponentId: string; timerAction: Timer.Action }
): SelfPacedPlayerState {
  const current = state.timers[action.timerComponentId];
  if (!current) return state;
  return {
    ...state,
    timers: {
      ...state.timers,
      [action.timerComponentId]: Timer.reducer(current, action.timerAction),
    },
  };
}

/**
 * Enter a new pin in the self-paced player, determining the initial view state.
 *
 * @param state The current player state
 * @param pinId The ID of the pin to enter
 * @returns The new state with the pin entered and initial view set
 */
export function newForegroundPinSelfPaced(
  state: InitializedSelfPacedPlayerStoreState,
  pinId: string
): InitializedSelfPacedPlayerStoreState {
  const pin = state.pins.find((p) => p.id === pinId);
  if (!pin) return state;

  const reducer = getSelfPacedPinReducer(pin.type);

  if (!reducer) {
    const existingView = state.pinState[pinId]?.view ?? null;
    return stateForNextView(state, pinId, existingView);
  }

  const historyView = state.pinState[pinId]?.view as never;
  const nextView = reducer.initial(state, historyView);

  return stateForNextView(state, pinId, nextView);
}
